/**
 * Componente PIPButton
 * 
 * Botão reutilizável para abrir janela Picture-in-Picture
 * Pode ser usado em OSCard, OSDetailsPage, ActiveSessionsWidget, etc.
 * 
 * Agora usa PIPContext global para persistir durante navegação
 * 
 * @author VISION DESK
 * @version 0.24.2
 */

import React from 'react';
import { Tv, AlertCircle } from 'lucide-react';
import { usePIPContext } from '@/contexts/PIPContext';
import { getBrowserInfo } from '@/hooks/usePIP';

interface PIPButtonProps {
  /**
   * ID do Job/Task
   */
  osId: string;
  
  /**
   * ID da sessão ativa
   */
  sessionId: string;
  
  /**
   * Título do Job/Task (para tooltip)
   */
  osTitle?: string;
  
  /**
   * Variante do botão (icon-only ou com texto)
   */
  variant?: 'icon-only' | 'with-text';
  
  /**
   * Tamanho do botão
   */
  size?: 'sm' | 'md' | 'lg';
  
  /**
   * Callback quando PIP é aberto
   */
  onOpen?: () => void;
  
  /**
   * Callback quando PIP é fechado
   */
  onClose?: () => void;
}

export const PIPButton: React.FC<PIPButtonProps> = ({
  osId,
  sessionId,
  osTitle,
  variant = 'icon-only',
  size = 'md',
  onOpen,
  onClose,
}) => {
  // Usar contexto global de PIP
  const { isPIPOpen, isPIPSupported, openPIP: openPIPGlobal, closePIP: closePIPGlobal, pipState } = usePIPContext();

  // Verificar se este PIP está aberto
  const isThisPIPOpen = isPIPOpen && pipState?.osId === osId && pipState?.sessionId === sessionId;

  const handleClick = async () => {
    if (isThisPIPOpen) {
      // Se já está aberto, fechar
      closePIPGlobal();
      onClose?.();
      return;
    }

    // Se outro PIP está aberto, fechar e abrir este
    if (isPIPOpen) {
      // console.log('🔄 PIP: Trocando PIP para outra OS...');
      closePIPGlobal();
    }

    // Verificar navegador
    const browserInfo = getBrowserInfo();
    
    // Avisar sobre fallback se não suporta
    if (!browserInfo.supportsNativePIP) {
      // console.log('⚠️ PIP: Navegador não suporta PIP nativo, usando fallback modal');
    }

    // Abrir PIP
    await openPIPGlobal(osId, sessionId, osTitle);
    onOpen?.();
  };

  // Classes baseadas no tamanho
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Tooltip
  const tooltip = osTitle
    ? `Abrir "${osTitle}" em Picture-in-Picture`
    : 'Abrir em Picture-in-Picture';

  return (
    <>
      {variant === 'icon-only' ? (
        <button
          onClick={handleClick}
          title={tooltip}
          className={`${sizeClasses[size]} rounded-lg transition-all duration-300
                     ${
                       isThisPIPOpen
                         ? 'bg-purple-500/30 text-purple-300 ring-2 ring-purple-500/50'
                         : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 hover:text-purple-300'
                     }`}
        >
          <Tv className={iconSizeClasses[size]} />
        </button>
      ) : (
        <button
          onClick={handleClick}
          className={`${sizeClasses[size]} px-4 rounded-lg transition-all duration-300
                     flex items-center gap-2 font-medium text-sm
                     ${
                       isThisPIPOpen
                         ? 'bg-purple-500/30 text-purple-300 ring-2 ring-purple-500/50'
                         : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 hover:text-purple-300'
                     }`}
        >
          <Tv className={iconSizeClasses[size]} />
          <span>{isThisPIPOpen ? 'Fechar PIP' : 'Abrir em PIP'}</span>
          {!isPIPSupported && (
            <span title="Modo fallback (modal)">
              <AlertCircle className="w-4 h-4" />
            </span>
          )}
        </button>
      )}
    </>
  );
};
